import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Num({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input type="number" step="0.5" min="0.5" className="field-input font-mono" value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testId} />
    </div>
  );
}

export function WorkingHours() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const [s, setS] = useState(null);
  useEffect(() => {
    if (data) setS(data);
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.put("/settings", s).then((r) => r.data),
    onSuccess: () => {
      toast.success("Working hours & SLA saved. Escalations recalculated.");
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!s) return <p className="text-zinc-600">Loading…</p>;
  const toggleDay = (d) => setS({ ...s, working_days: s.working_days.includes(d) ? s.working_days.filter((x) => x !== d) : [...s.working_days, d].sort() });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="grid gap-6 lg:grid-cols-2"
      data-testid="working-hours-form"
    >
      <section className="brut-card space-y-5 p-5 sm:p-6">
        <h3 className="font-display text-xl font-bold">Working hours</h3>
        <p className="text-sm text-zinc-600">SLA timers only tick during these hours. Everything is calculated in the selected timezone.</p>
        <div>
          <label className="field-label">Working days</label>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d, i) => (
              <button
                type="button"
                key={d}
                onClick={() => toggleDay(i)}
                className={`rounded-full border-2 border-black px-4 py-2 text-sm font-bold transition-colors ${s.working_days.includes(i) ? "bg-black text-white" : "bg-white hover:bg-[#FEF9C3]"}`}
                data-testid={`working-day-${d.toLowerCase()}`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="field-label">Start</label>
            <input type="time" className="field-input font-mono" value={s.start_time} onChange={(e) => setS({ ...s, start_time: e.target.value })} data-testid="start-time-input" />
          </div>
          <div>
            <label className="field-label">End</label>
            <input type="time" className="field-input font-mono" value={s.end_time} onChange={(e) => setS({ ...s, end_time: e.target.value })} data-testid="end-time-input" />
          </div>
        </div>
        <div>
          <label className="field-label">Timezone (IANA)</label>
          <input className="field-input font-mono" value={s.timezone} onChange={(e) => setS({ ...s, timezone: e.target.value })} data-testid="timezone-input" />
        </div>
      </section>

      <section className="brut-card space-y-5 p-5 sm:p-6">
        <h3 className="font-display text-xl font-bold">Escalation thresholds (working hours)</h3>
        <p className="text-sm text-zinc-600">Unresolved tickets move up a level as working hours pass. The SLA due date shown to customers uses the L3 threshold. L3 and Out-of-SLA raise the priority alarm.</p>
        <div className="grid grid-cols-2 gap-4">
          <Num label="L1 after" value={s.l1_hours} onChange={(v) => setS({ ...s, l1_hours: v })} testId="l1-hours-input" />
          <Num label="L2 after (notify admin)" value={s.l2_hours} onChange={(v) => setS({ ...s, l2_hours: v })} testId="l2-hours-input" />
          <Num label="L3 / SLA due after" value={s.l3_hours} onChange={(v) => setS({ ...s, l3_hours: v })} testId="l3-hours-input" />
          <Num label="Out-of-SLA after" value={s.out_of_sla_hours} onChange={(v) => setS({ ...s, out_of_sla_hours: v })} testId="out-of-sla-hours-input" />
          <Num label="Waiting-on-student reminder after" value={s.waiting_reminder_hours ?? 18} onChange={(v) => setS({ ...s, waiting_reminder_hours: v })} testId="waiting-reminder-hours-input" />
        </div>
        <button type="submit" className="btn-primary w-full" disabled={save.isPending} data-testid="save-settings-button">
          {save.isPending ? "Saving…" : "Save settings"}
        </button>
      </section>
    </form>
  );
}
